import { useCallback, useState } from "react";
import { v4 as uuidv4 } from 'uuid'; // FIX: Import uuidv4
import { useHotkeys } from "./useHotkeys";
import { CustomNode, CustomEdge } from "../types";
import { useReactFlow, getConnectedEdges, XYPosition, Node, Edge } from 'reactflow'; // Import Node and Edge types
import useStore from '../store/useStore'; // Import Zustand store
import { useActionSnackbar } from '../contexts/ActionSnackbarContext'; // Changed to relative path

interface UseClipboardReturn {
  copy: () => void;
  cut: () => void;
  paste: (position?: XYPosition, message?: string, targetGroupId?: string) => void; // Allow optional position, message, and target group ID
  duplicate: () => void;
}

export function useClipboard(): UseClipboardReturn {
  // const { state, onNodesChange, onEdgesChange } = useCanvas(); // Remove useCanvas usage

  // --- Replace localStorage with in-memory buffer ---
  const [bufferedNodes, setBufferedNodes] = useState<CustomNode[]>([]);
  const [bufferedEdges, setBufferedEdges] = useState<CustomEdge[]>([]);
  // --- End Replace ---

  const { getNodes, getEdges, screenToFlowPosition, fitView } = useReactFlow<CustomNode, CustomEdge>(); // Use useReactFlow, add fitView
  const { showSnackbar } = useActionSnackbar(); // Initialize snackbar hook
  // Access store actions directly
  const storeSetNodes = useStore((state) => state.setNodes);
  const storeSetEdges = useStore((state) => state.setEdges);
  const storeOnNodesChange = useStore((state) => state.onNodesChange);
  const storeOnEdgesChange = useStore((state) => state.onEdgesChange);
  const takeSnapshot = useStore((state) => state.takeSnapshot);
  const storeNodes = useStore((state) => state.nodes); // Get nodes from store


  const getSelectedNodes = useCallback((): CustomNode[] => {
    // Use nodes from the store directly
    return storeNodes.filter((node) => node.selected);
  }, [storeNodes]); // Depend on storeNodes

  const getEdgesBetweenNodes = useCallback(
    (nodes: CustomNode[]): CustomEdge[] => {
      const nodeIds = new Set(nodes.map((node) => node.id));
      // Use getConnectedEdges from reactflow, pass nodes and all edges
      return getConnectedEdges(nodes, getEdges()).filter(
        (edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target)
      );
    },
    [getEdges], // Depend on getEdges from useReactFlow
  );

  // Remove localStorage functions
  // const copyToClipboard = useCallback((data: ClipboardData) => { ... });
  // const getFromClipboard = useCallback((): ClipboardData | null => { ... });

  // --- REMOVED generateNewIds function ---

  const copy = useCallback(() => {
    const initiallySelectedNodes = getSelectedNodes(); // These are CustomNode[]
    if (!initiallySelectedNodes.length) return;

    const allNodes = getNodes(); // Get all nodes (Node<CustomNode>[]) for finding descendants

    // Helper function to recursively find all descendant node IDs
    // Operates on the full list of nodes from getNodes()
    const getDescendantIds = (groupId: string, allNodesForSearch: Node<CustomNode>[]): string[] => {
      const descendants: string[] = [];
      const children = allNodesForSearch.filter(n => n.parentId === groupId);
      children.forEach(child => {
        descendants.push(child.id);
        if (child.type === 'group') {
          descendants.push(...getDescendantIds(child.id, allNodesForSearch)); // Pass allNodes down
        }
      });
      return descendants;
    };

    const nodeIdsToCopy = new Set<string>(initiallySelectedNodes.map(n => n.id));

    // Add descendants for any selected groups
    initiallySelectedNodes.forEach(node => {
      if (node.type === 'group') {
        // Pass the full allNodes list to the helper
        const descendantIds = getDescendantIds(node.id, allNodes);
        descendantIds.forEach(id => nodeIdsToCopy.add(id));
      }
    });

    // Get the actual node objects to copy (still Node<CustomNode>[])
    const nodesToBuffer = allNodes.filter(node => nodeIdsToCopy.has(node.id));

    // Cast to CustomNode[] for getEdgesBetweenNodes and buffer
    const nodesToCopy = nodesToBuffer.map(n => n as unknown as CustomNode);

    // Get edges connecting the nodes within the final list
    const edgesToCopy = getEdgesBetweenNodes(nodesToCopy);

    // --- Use in-memory buffer ---
    setBufferedNodes(nodesToCopy); // Store as CustomNode[]
    setBufferedEdges(edgesToCopy);
    // --- End Use ---
    console.log('[Clipboard] Copied nodes:', nodesToCopy.map(n => n.id));
    console.log('[Clipboard] Copied edges:', edgesToCopy.map(e => e.id));
    showSnackbar(`Copied ${nodesToCopy.length} node(s)`);
  }, [getSelectedNodes, getEdgesBetweenNodes, showSnackbar, getNodes]);

  const cut = useCallback(() => {
    takeSnapshot(); // Snapshot before cut
    const initiallySelectedNodes = getSelectedNodes(); // These are CustomNode[]
    if (!initiallySelectedNodes.length) return;

    const allNodes = getNodes(); // Get all nodes (Node<CustomNode>[]) for finding descendants

    // Helper function to recursively find all descendant node IDs (same as in copy)
    const getDescendantIds = (groupId: string, allNodesForSearch: Node<CustomNode>[]): string[] => {
      const descendants: string[] = [];
      const children = allNodesForSearch.filter(n => n.parentId === groupId);
      children.forEach(child => {
        descendants.push(child.id);
        if (child.type === 'group') {
          descendants.push(...getDescendantIds(child.id, allNodesForSearch));
        }
      });
      return descendants;
    };

    const nodeIdsToCut = new Set<string>(initiallySelectedNodes.map(n => n.id));

    // Add descendants for any selected groups
    initiallySelectedNodes.forEach(node => {
      if (node.type === 'group') {
        const descendantIds = getDescendantIds(node.id, allNodes);
        descendantIds.forEach(id => nodeIdsToCut.add(id));
      }
    });

    // Get the actual node objects to cut and buffer (still Node<CustomNode>[])
    // These are the nodes that will be removed and also stored in the buffer
    const nodesToCutAndBuffer = allNodes.filter(node => nodeIdsToCut.has(node.id));

    // Cast to CustomNode[] for getEdgesBetweenNodes and buffer
    const nodesForBuffer = nodesToCutAndBuffer.map(n => n as unknown as CustomNode);

    // Get edges connecting the nodes within the final list of nodes to cut
    const edgesToCutAndBuffer = getEdgesBetweenNodes(nodesForBuffer);

    // --- Use in-memory buffer ---
    setBufferedNodes(nodesForBuffer); // Store as CustomNode[]
    setBufferedEdges(edgesToCutAndBuffer);
    // --- End Use ---

    // Remove all identified nodes (selected + descendants)
    storeOnNodesChange(
      Array.from(nodeIdsToCut).map((id) => ({ // Use nodeIdsToCut
        type: "remove",
        id: id,
      })),
    );
    // Remove all identified edges
    storeOnEdgesChange(
      edgesToCutAndBuffer.map((edge) => ({ // Use edgesToCutAndBuffer
        type: "remove",
        id: edge.id,
      })),
    );
    console.log('Cut nodes:', Array.from(nodeIdsToCut));
    showSnackbar(`Cut ${nodeIdsToCut.size} item(s)`);
  }, [
    getSelectedNodes,
    getEdgesBetweenNodes,
    storeOnNodesChange,
    storeOnEdgesChange,
    takeSnapshot,
    showSnackbar,
    getNodes, // Added getNodes
  ]); // Removed copyToClipboard dependency

  const paste = useCallback((
    // Default paste position calculation using screenToFlowPosition
    position?: XYPosition, // Optional: Target position on canvas (if not pasting into group)
    message: string = `Pasted ${bufferedNodes.length} item(s)`, // Default message
    targetGroupId?: string // Optional: ID of the group to paste into
  ) => {
    if (!bufferedNodes.length) return;
    takeSnapshot(); // Snapshot before paste

    const allNodes = getNodes(); // Get all current nodes (Node<CustomNode>[])
    const { addNode } = useStore.getState(); // Get addNode from store
    const idMap = new Map<string, string>(); // Map old IDs to new IDs
    const newTopLevelNodeIds: string[] = []; // Track IDs of newly pasted top-level nodes for selection

    // --- Determine Paste Position ---
    let pastePosition: XYPosition;
    let isPastingIntoGroup = false;
    const targetGroup = targetGroupId ? allNodes.find(n => n.id === targetGroupId) : undefined;

    if (targetGroup) {
      isPastingIntoGroup = true;
      const groupPadding = 20;
      // Position relative to group origin
      pastePosition = { x: groupPadding, y: groupPadding };
      console.log(`[Clipboard] Pasting into group ${targetGroupId} at relative position`, pastePosition);
    } else {
      if (targetGroupId) {
        console.warn(`[Clipboard] Target group ${targetGroupId} not found. Pasting onto canvas.`);
      }
      // Default paste onto canvas
      const defaultOffset = 50;
      pastePosition = position ?? {
        x: (allNodes[0]?.position.x ?? 0) + defaultOffset,
        y: (allNodes[0]?.position.y ?? 0) + defaultOffset
      };
      console.log(`[Clipboard] Pasting onto canvas at position`, pastePosition);
    }

    // Find the top-left corner of the *buffered* nodes for relative positioning
    const bufferedNodeIds = new Set(bufferedNodes.map(n => n.id));
    const minXBuffered = Math.min(...bufferedNodes.map((s) => s.position.x));
    const minYBuffered = Math.min(...bufferedNodes.map((s) => s.position.y));

    // --- Recursive function to paste children ---
    const pasteChildren = (originalParentId: string, newParentId: string) => {
      const childrenToPaste = bufferedNodes.filter(n => n.parentId === originalParentId);

      childrenToPaste.forEach(childNode => {
        const newChildId = `${childNode.type}-${uuidv4()}`; // Use uuid for robustness
        idMap.set(childNode.id, newChildId);

        const newChildNode: CustomNode = {
          ...(childNode as CustomNode), // Cast needed here
          id: newChildId,
          parentId: newParentId, // Assign the NEW parent ID
          position: { ...childNode.position }, // Keep original relative position
          selected: false, // Children are not selected initially
          extent: 'parent',
        };
        console.log(`[Clipboard] Pasting child ${newChildId} (original: ${childNode.id}) into parent ${newParentId}`);
        addNode(newChildNode);

        if (childNode.type === 'group') {
          pasteChildren(childNode.id, newChildId); // Recurse if child is a group
        }
      });
    };

    // --- Process Top-Level Buffered Nodes ---
    bufferedNodes.forEach(node => {
      // Process only nodes whose parent is NOT part of the buffered selection
      if (!node.parentId || !bufferedNodeIds.has(node.parentId)) {
        const newId = `${node.type}-${uuidv4()}`; // Use uuid for robustness
        idMap.set(node.id, newId);
        newTopLevelNodeIds.push(newId); // Track for selection later

        // Calculate position offset from the buffered top-left
        const offsetX = node.position.x - minXBuffered;
        const offsetY = node.position.y - minYBuffered;

        let finalNodePosition: XYPosition;
        let finalParentId: string | undefined = undefined;
        let finalExtent: 'parent' | undefined = undefined;

        if (isPastingIntoGroup && targetGroup) {
          finalNodePosition = { x: pastePosition.x + offsetX, y: pastePosition.y + offsetY };
          finalParentId = targetGroupId; // Assign target group as parent
          finalExtent = 'parent';
          console.log(`[Clipboard] Pasting top-level ${newId} (original: ${node.id}) into group ${targetGroupId}`);
        } else {
          finalNodePosition = { x: pastePosition.x + offsetX, y: pastePosition.y + offsetY };
          console.log(`[Clipboard] Pasting top-level ${newId} (original: ${node.id}) onto canvas`);
        }

        const newNode: CustomNode = {
          ...(node as CustomNode), // Cast needed here
          id: newId,
          position: finalNodePosition,
          parentId: finalParentId,
          extent: finalExtent,
          selected: false, // Will be selected later
        };
        addNode(newNode);

        // If it's a group, paste its children recursively
        if (node.type === 'group') {
          pasteChildren(node.id, newId);
        }
      }
    });

    // --- Paste Edges ---
    const edgesToAdd: CustomEdge[] = [];
    bufferedEdges.forEach(edge => {
      const newSourceId = idMap.get(edge.source);
      const newTargetId = idMap.get(edge.target);

      // Only add edges where both source and target were successfully pasted
      if (newSourceId && newTargetId) {
        const originalSourceNode = bufferedNodes.find(n => n.id === edge.source);
        const originalTargetNode = bufferedNodes.find(n => n.id === edge.target);

        // Update handle IDs based on new node IDs if they were groups
        let newSourceHandle = edge.sourceHandle;
        if (originalSourceNode?.type === 'group' && edge.sourceHandle) {
           newSourceHandle = edge.sourceHandle.replace(edge.source, newSourceId);
        }
        let newTargetHandle = edge.targetHandle;
        if (originalTargetNode?.type === 'group' && edge.targetHandle) {
           newTargetHandle = edge.targetHandle.replace(edge.target, newTargetId);
        }

        const newEdge: CustomEdge = {
          ...edge,
          id: `${edge.id}-copy-${uuidv4().substring(0, 8)}`, // More unique edge ID
          source: newSourceId,
          target: newTargetId,
          sourceHandle: newSourceHandle,
          targetHandle: newTargetHandle,
          selected: false,
        };
        edgesToAdd.push(newEdge);
        console.log(`[Clipboard] Adding edge ${newEdge.id} between ${newSourceId} and ${newTargetId}`);
      } else {
         console.warn(`[Clipboard] Skipping edge ${edge.id} because source/target node was not pasted.`);
      }
    });

    // --- Update State ---
    // Deselect current selection
    storeSetNodes((nds) => nds.map((n: Node) => ({ ...n, selected: false })));
    const currentEdges = getEdges(); // Get current edges
    const deselectedEdges = currentEdges.map((e: Edge<CustomEdge>) => ({ ...e, selected: false })) as Edge[]; // FIX: Create deselected array
    storeSetEdges(deselectedEdges); // FIX: Pass the array directly

    // Add the new edges
    if (edgesToAdd.length > 0) {
      const finalEdges = [...deselectedEdges, ...edgesToAdd] as Edge[]; // FIX: Combine and cast
      storeSetEdges(finalEdges); // FIX: Pass the combined array directly
    }

    // Select the newly pasted top-level nodes
    storeSetNodes((nds) =>
      nds.map((n: Node) => ({
        ...n,
        selected: newTopLevelNodeIds.includes(n.id),
      }))
    );

    console.log('Pasted nodes:', newTopLevelNodeIds);

    // Clear buffer after paste? Optional, allows multiple pastes. Let's keep it buffered.
    // setBufferedNodes([]);
    // setBufferedEdges([]);
    let finalMessage = message;
    if (isPastingIntoGroup && targetGroup) {
      const groupName = (targetGroup.data as any)?.label || (targetGroup.data as any)?.name || targetGroup.id; // Attempt to get a display name
      finalMessage = `Pasted ${bufferedNodes.length} item(s) into group '${groupName}'`;
    }
    showSnackbar(finalMessage); // Show snackbar with provided or default message

    // Fit view to the newly pasted nodes
    const allNewNodeIds = Array.from(idMap.values());
    if (allNewNodeIds.length > 0 && fitView) {
      // Wait for the next tick to ensure nodes are rendered before fitting view
      setTimeout(() => {
        fitView({ nodes: allNewNodeIds.map(id => ({ id })), padding: 0.1, duration: 200 });
      }, 0);
    }

  }, [
    bufferedNodes,
    bufferedEdges,
    // FIX: Remove generateNewIds from dependencies
    storeSetNodes,
    storeSetEdges,
    screenToFlowPosition, // Add dependency
    takeSnapshot, // Add snapshot dependency
    getNodes,
    showSnackbar,
    getEdges, // Add getEdges dependency
    fitView, // Add fitView dependency
  ]);

  const duplicate = useCallback(() => {
    // Duplicate should ideally position relative to the selected nodes, not just paste at offset
    const selectedNodes = getSelectedNodes();
    if (!selectedNodes.length) return;

    const selectedEdges = getEdgesBetweenNodes(selectedNodes);

    // Calculate center or top-left of selected nodes for positioning duplicate
    const positionsX = selectedNodes.map(n => n.position.x);
    const positionsY = selectedNodes.map(n => n.position.y);
    const minX = Math.min(...positionsX);
    const minY = Math.min(...positionsY);
    const duplicatePosition = { x: minX + 30, y: minY + 30 }; // Offset from original

    // Temporarily set buffer for paste logic
    setBufferedNodes(selectedNodes);
    setBufferedEdges(selectedEdges);

    // Call paste with calculated position and specific message
    paste(duplicatePosition, `Duplicated ${selectedNodes.length} item(s)`);

    // Restore original buffer if needed (or maybe duplicate shouldn't use the main buffer?)
    // For simplicity, let's assume duplicate overwrites the buffer like a copy action.
    // setBufferedNodes(originalBufferedNodes);
    // setBufferedEdges(originalBufferedEdges);

  }, [getSelectedNodes, getEdgesBetweenNodes, paste, bufferedNodes, bufferedEdges]); // Depend on paste

  // Keep existing useHotkeys integration, add 'meta' for Mac
  useHotkeys([
    {
      key: "c",
      modifiers: ["ctrl", "meta"],
      description: "Copy selected elements",
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        copy();
      },
      preventDefault: true,
    },
    {
      key: "x",
      modifiers: ["ctrl", "meta"],
      description: "Cut selected elements",
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        cut();
      },
      preventDefault: true,
    },
    {
      key: "v",
      modifiers: ["ctrl", "meta"],
      description: "Paste elements",
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        const currentSelectedNodes = getSelectedNodes(); // Renamed to avoid conflict with outer scope
        let targetGroupId: string | undefined = undefined;
        let pasteMessage: string | undefined = undefined;

        // Check if exactly one node is selected and it's a group
        if (currentSelectedNodes.length === 1 && currentSelectedNodes[0].type === 'group') {
          targetGroupId = currentSelectedNodes[0].id;
          const groupName = currentSelectedNodes[0].data?.label || currentSelectedNodes[0].data?.name || targetGroupId;
          pasteMessage = `Pasted ${bufferedNodes.length} item(s) into group '${groupName}'`;
          console.log(`[Clipboard] Pasting into selected group: ${targetGroupId}`);
        }

        // Call paste, providing targetGroupId and potentially a custom message
        paste(undefined, pasteMessage, targetGroupId);
      },
      preventDefault: true,
    },
    {
      key: "d",
      modifiers: ["ctrl", "meta"],
      description: "Duplicate selected elements",
      handler: (e: KeyboardEvent) => {
        e.preventDefault();
        duplicate();
      },
      preventDefault: true,
    },
  ]);

  return {
    copy,
    cut,
    paste,
    duplicate,
  };
}
